/**
 * Per-chapter media fallback state machine.
 * - hold-last-frame on fling-scroll miss (never blank)
 * - error → poster + retry button
 */

export type FallbackState = 'idle' | 'playing' | 'hold' | 'error';

export interface FallbackUi {
  /** Poster image (or container) shown on error / before ready. */
  posterEl: HTMLElement;
  /** Optional retry control; shown only in error. */
  retryBtn: HTMLButtonElement | null;
  /** Video element being managed. */
  videoEl: HTMLVideoElement;
  /** Optional error message node. */
  errorEl?: HTMLElement | null;
}

export interface FallbackHandlers {
  onRetry?: () => void | Promise<void>;
  onStateChange?: (state: FallbackState) => void;
}

export class ChapterFallback {
  private state: FallbackState = 'idle';
  private lastGoodTime = 0;
  private readonly ui: FallbackUi;
  private readonly handlers: FallbackHandlers;

  constructor(ui: FallbackUi, handlers: FallbackHandlers = {}) {
    this.ui = ui;
    this.handlers = handlers;
    this.bindRetry();
    this.render();
  }

  getState(): FallbackState {
    return this.state;
  }

  getLastGoodTime(): number {
    return this.lastGoodTime;
  }

  /** Call when a seek/frame is known good. */
  markGoodFrame(currentTime: number): void {
    if (Number.isFinite(currentTime) && currentTime >= 0) {
      this.lastGoodTime = currentTime;
    }
    if (this.state === 'hold' || this.state === 'idle') {
      this.transition('playing');
    }
  }

  /**
   * Hold last good frame on miss (decode stall / seek miss).
   * Never clears the frame to blank.
   */
  holdLastFrame(): void {
    if (this.state === 'error') return;
    const t = this.lastGoodTime;
    try {
      if (
        this.ui.videoEl.readyState >= 1 &&
        Number.isFinite(t) &&
        Math.abs(this.ui.videoEl.currentTime - t) > 0.001
      ) {
        this.ui.videoEl.currentTime = t;
      }
    } catch {
      /* metadata may be missing */
    }
    this.transition('hold');
  }

  /** Enter per-chapter error: show poster + retry. */
  enterError(message?: string): void {
    this.transition('error');
    if (this.ui.errorEl && message) {
      this.ui.errorEl.textContent = message;
    }
  }

  /** Leave error / hold back to playing after successful load. */
  recover(): void {
    this.transition('playing');
  }

  /** Explicit retry path (also bound to button). */
  async retry(): Promise<void> {
    if (this.state !== 'error') return;
    this.transition('idle');
    try {
      await this.handlers.onRetry?.();
      this.transition('playing');
    } catch (err) {
      this.enterError(err instanceof Error ? err.message : 'Retry failed');
    }
  }

  private bindRetry(): void {
    const btn = this.ui.retryBtn;
    if (!btn) return;
    btn.addEventListener('click', () => {
      void this.retry();
    });
  }

  private transition(next: FallbackState): void {
    if (this.state === next) {
      this.render();
      return;
    }
    this.state = next;
    this.render();
    this.handlers.onStateChange?.(next);
  }

  private render(): void {
    const { posterEl, retryBtn, videoEl, errorEl } = this.ui;
    const state = this.state;

    const showPoster = state === 'idle' || state === 'error';
    const showRetry = state === 'error';
    const showVideo = state === 'playing' || state === 'hold';

    posterEl.hidden = !showPoster;
    posterEl.style.visibility = showPoster ? 'visible' : 'hidden';

    if (showVideo) {
      videoEl.style.visibility = 'visible';
    } else if (state === 'error') {
      // Keep last frame under poster if possible; hide video chrome
      videoEl.style.visibility = 'hidden';
    }

    if (retryBtn) {
      retryBtn.hidden = !showRetry;
      retryBtn.style.display = showRetry ? '' : 'none';
    }

    if (errorEl) {
      errorEl.hidden = state !== 'error';
    }

    posterEl.dataset.fallbackState = state;
    videoEl.dataset.fallbackState = state;
  }
}
